import {
  EXTERNAL_LINK_ATTRS,
  avisoPestanaNueva,
  type DeepLink,
} from '@ops/contracts'
import { useId, type ReactNode } from 'react'

import { cx } from '../lib/cx'
import { Button } from './Button'
import { Icon } from './Icon'

/**
 * El único componente del panel autorizado a escribir un `<a target="_blank">`.
 * Ningún otro debe hacerlo a mano: si el aviso accesible del salto de sistema se
 * arma en veinte lugares, en algún lugar falta.
 *
 * Recibe un `DeepLink | null`:
 *
 *  - **objeto** → link al registro exacto del sistema de origen, en pestaña
 *    nueva, con `rel="noopener noreferrer"` y el aviso `(se abre en Plane,
 *    pestaña nueva)` en un `sr-only`. El aviso nunca queda solo en un icono.
 *  - **null** → estado **"sin vincular"** con la acción de vincularlo, igual que
 *    un KPI sin dato. Nunca se renderiza un `<a>` a una URL incompleta: un link
 *    que lleva a una 404 es peor que no tener link.
 */
export interface ExternalLinkProps {
  /** `null` = sin vincular. Los builders del contrato devuelven null solos. */
  link: DeepLink | null
  /** Texto del link. Por defecto, el `label` del DeepLink. */
  children?: ReactNode
  /** Qué sistema falta vincular, para el estado null. Ej. "Stripe". */
  sistemaFaltante?: string
  /** Acción de vincular. Sin handler el estado null no ofrece botón. */
  onVincular?: () => void
  /** Texto del botón de vincular. */
  textoVincular?: string
  variante?: 'texto' | 'chip' | 'boton'
  /** false oculta el icono de salida. El aviso accesible se mantiene. */
  mostrarIcono?: boolean
  className?: string
}

const VARIANTE: Record<NonNullable<ExternalLinkProps['variante']>, string> = {
  texto:
    'inline-flex max-w-full items-center gap-1 text-meta font-semibold text-accent-text underline decoration-from-font underline-offset-2 hover:text-text',
  chip: 'inline-flex max-w-full items-center gap-1.5 rounded border border-border bg-surface px-1.5 py-0.5 text-micro font-semibold text-text transition-colors duration-150 ease-brand hover:border-n300 hover:bg-n100',
  boton:
    'inline-flex max-w-full items-center gap-1.5 rounded-md border border-border bg-surface px-3 py-1 text-meta font-semibold text-text transition-colors duration-150 ease-brand hover:border-n300 hover:bg-n100',
}

export function ExternalLink({
  link,
  children,
  sistemaFaltante,
  onVincular,
  textoVincular = 'Vincular',
  variante = 'texto',
  mostrarIcono = true,
  className,
}: ExternalLinkProps) {
  const estadoId = useId()

  if (link === null) {
    const que = sistemaFaltante === undefined ? '' : ` en ${sistemaFaltante}`

    return (
      <span
        data-testid="external-link-sin-vincular"
        className={cx(
          'inline-flex max-w-full flex-wrap items-center gap-1.5 text-micro text-muted-strong',
          className,
        )}
      >
        <Icon name="sin-vincular" size={11} />
        <span id={estadoId}>Sin vincular{que}</span>
        {onVincular !== undefined ? (
          /*
           * El sistema que falta va como **descripción**, no pegado al nombre.
           * Concatenar el sufijo daba nombres redundantes: con
           * `textoVincular="Vincular en Admin"` y `sistemaFaltante="Stripe"` el
           * botón se anunciaba "Vincular en Admin en Stripe". Con
           * `aria-describedby` el nombre queda "Vincular en Admin" y el lector
           * agrega "Sin vincular en Stripe" como contexto, que es exactamente lo
           * que ya dice el texto de al lado.
           */
          <Button
            variante="chico"
            onClick={onVincular}
            aria-describedby={estadoId}
          >
            {textoVincular}
          </Button>
        ) : null}
      </span>
    )
  }

  return (
    <a
      href={link.url}
      target={EXTERNAL_LINK_ATTRS.target}
      rel={EXTERNAL_LINK_ATTRS.rel}
      data-testid="external-link"
      data-system={link.system}
      className={cx(VARIANTE[variante], className)}
    >
      <span className="min-w-0 truncate">{children ?? link.label}</span>
      {mostrarIcono ? (
        <Icon name="externo" size={variante === 'texto' ? 11 : 10} className="opacity-60" />
      ) : null}
      {/* El salto de sistema se avisa con palabras, nunca solo con el icono. */}
      <span className="sr-only">{avisoPestanaNueva(link)}</span>
    </a>
  )
}
